import * as React from "react";
import { useBlocker, useLocation, type Location } from "react-router-dom";
import { ConfirmDialog } from "@/components/ui/alert-dialog";

/**
 * Leaving a page with unsaved changes asks first (ACL-404), however the page is left: a link or a
 * button in the console, the browser's Back or Forward, the mouse's back button, a trackpad swipe,
 * a reload or a closed tab. One guard every editing page uses (Cadences, Calendars, Intake flow),
 * so a new one gets all of it by calling `useLeaveGuard`.
 *
 * Inside the console it is React Router's `useBlocker`, which holds any navigation, a history step
 * included, until the page's question is answered; it needs the data router `App.tsx` makes. A
 * reload or a closed tab is the browser's own question (`beforeunload`), since no page may word it.
 *
 * Before this each page caught clicks on links itself, which the browser's Back never is, and the
 * intake flow did not even do that.
 */

/** Whether a move from `from` to `to` leaves the page. Only a new path does: a page's tabs and
 *  filters live in its query, and moving between them keeps the page and its changes. */
export function leavesPage(from: Pick<Location, "pathname">, to: Pick<Location, "pathname">): boolean {
  return from.pathname !== to.pathname;
}

export interface LeaveGuard {
  /** The question, rendered once by the page. */
  dialog: React.ReactElement;
  /** Let the page's own next move through without asking: the one it makes after a save. */
  release: () => void;
}

/**
 * Ask before leaving while `unsaved`. `description` says what would be lost ("The changes to this
 * cadence are not saved. Leaving drops them."). Cancel keeps the person on the page with their
 * changes; Leave goes where they were going, a step back included.
 */
export function useLeaveGuard(unsaved: boolean, description: string): LeaveGuard {
  const location = useLocation();
  const released = React.useRef(false);
  // A release is for one move. Once the address has changed it is spent.
  React.useEffect(() => {
    released.current = false;
  }, [location.key]);

  const blocker = useBlocker(
    React.useCallback(
      ({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) =>
        unsaved && !released.current && leavesPage(currentLocation, nextLocation),
      [unsaved],
    ),
  );

  // Saved, or undone back to what was saved, while the question was up: nothing is lost any more.
  React.useEffect(() => {
    if (blocker.state === "blocked" && !unsaved) blocker.reset();
  }, [blocker, unsaved]);

  React.useEffect(() => {
    if (!unsaved) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsaved]);

  const release = React.useCallback(() => {
    released.current = true;
  }, []);

  const dialog = (
    <ConfirmDialog
      open={blocker.state === "blocked"}
      // Cancel, Esc or a click outside. Leave never comes here: it is not the dialog's own close,
      // and a reset after `proceed` would hold the step back it is taking.
      onOpenChange={(open) => {
        if (!open && blocker.state === "blocked") blocker.reset();
      }}
      title="Leave without saving?"
      description={description}
      confirmLabel="Leave"
      destructive
      onConfirm={() => {
        if (blocker.state === "blocked") blocker.proceed();
      }}
    />
  );

  return { dialog, release };
}
